import { eventId } from "@shared/analytics/walletEvents";
import { createUseSquadsMultisig, type SquadsMultisigStatus } from "@shared/yaas/squadsMultisig";
import { FEATURE_FLAGS } from "@solomon-apps/config";
import { useRef, useState } from "react";

import type { EnrollmentJourney } from "@/services/analytics/enrollment";

import {
    preflightRetailSquadsMultisig,
    type SquadsDestinationEligibility,
    squadsDestinationStatus
} from "./retailSquadsPreflight";

const useRetailSquadsMultisig = createUseSquadsMultisig(preflightRetailSquadsMultisig);

export interface SquadsDestinationVerification extends SquadsDestinationEligibility {
    status: SquadsMultisigStatus;
    retry: () => void;
}

const NOT_CHECKED: SquadsDestinationEligibility = {
    canSubmit: true,
    message: null,
    isError: false,
    canRetry: false
};

/** Squads eligibility only; ordinary destinations still need the form's
 * address validation and the existing signed enrollment ceremony.
 *
 * `enabled` false runs no preflight and never gates submit: the form
 * uses it when no destination is offered, so the destination is the
 * connected wallet, which cannot be a Squads vault. An empty address
 * keeps the underlying hook idle, so nothing is fetched and no check
 * event is sent. */
export function useSquadsDestinationVerification(
    address: string,
    connectedWallet: string,
    analytics?: EnrollmentJourney,
    enabled = true,
    squadsEnabled = FEATURE_FLAGS.retailSquadsMultisig
): SquadsDestinationVerification {
    const [retryNonce, setRetryNonce] = useState(0);
    const observed = useRef<string | null>(null);
    const observedRetry = useRef(0);
    const status = useRetailSquadsMultisig(
        enabled ? address.trim() || connectedWallet : "",
        connectedWallet || null,
        retryNonce,
        (cached) => {
            const key = `${analytics?.id}:${address.trim()}:${connectedWallet}:${retryNonce}`;
            if (observed.current === key) return (): void => undefined;
            const trigger = retryNonce !== observedRetry.current ? "retry" : observed.current ? "edit" : "initial";
            observedRetry.current = retryNonce;
            observed.current = key;
            const checkId = eventId();
            const started = performance.now();
            const properties = {
                check_type: "destination" as const,
                check_id: checkId,
                trigger,
                check_source: cached ? ("cached" as const) : ("fresh" as const)
            };
            analytics?.send("enroll:check_started", properties);
            return (result): void => {
                const gate = squadsDestinationStatus(result, squadsEnabled);
                analytics?.send(result.status === "lookup-failed" ? "enroll:check_failed" : "enroll:check_completed", {
                    ...properties,
                    can_submit: gate.canSubmit,
                    duration_ms: performance.now() - started,
                    result:
                        !squadsEnabled &&
                        ["success", "vault-unknown-to-system", "unsupported-version"].includes(result.status)
                            ? "feature_disabled"
                            : result.status === "success"
                              ? result.signerStatus.replaceAll("-", "_")
                              : result.status === "not-found"
                                ? !address.trim() || address.trim() === connectedWallet
                                    ? "self"
                                    : "ordinary_wallet"
                                : result.status.replaceAll("-", "_"),
                    error_code: result.status === "lookup-failed" ? "destination_check_failed" : undefined
                });
            };
        }
    );
    return {
        status,
        ...(enabled ? squadsDestinationStatus(status, squadsEnabled) : NOT_CHECKED),
        retry: () => setRetryNonce((nonce) => nonce + 1)
    };
}
